import { HTMLAttributes, forwardRef, useCallback, useEffect, useState } from 'react';
import { Switch, styled } from '@mui/material';
import { Icon } from '@skodaflow/web-library';

/**
 * @interface CardListProps
 * @description Base definition of SkodaFlow CardList independent on core implementation.
 * Properties are inherited from HTML Div element.
 */
export interface CardListProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Control the clicked state. The state can be controlled from outside if isClickable is true.
   */
  isClicked?: boolean;
  /**
   * Control the clicked state. The state can be controlled from outside if isClickable is true.
   */
  isChecked?: boolean;
  /**
   * If true the container can be clicked.
   */
  isClickable?: boolean;
  /**
   * Additional onClickCallback when the CardList is clicked. The onClickCallback works only if isClickable is true.
   */
  onClickCallback?: (state: boolean) => void;
  /**
   * Additional onCheckCallback when the CardList is clicked. The onClickCallback works only if isClickable is true.!!!!!!!!!!!!
   */
  onCheckCallback?: (state: boolean) => void;
  /**
   * If true, adds a star icon to mark the TOP item in the list
   */
  isTopVariant?: boolean;
  /**
   * If true, hides the item's label and reduces the height of the item
   */
  isSmallVariant?: boolean;
  /**
   * label for CardList Item. @default ""
   */
  label?: string;
  /**
   * List Item Title.  @default "List Item Title"
   */
  listItemTitle?: string;
  /**
   * width for CardList.  @default 100%
   */
  width?: string;
  /**
   * Display checkbox, right arrow or none at right position
   */
  rightContent?: 'checkbox' | 'chevron-right';
}

export const CardList = forwardRef<HTMLDivElement, CardListProps>(
  (
    {
      children,
      isClickable,
      onClickCallback,
      onCheckCallback,
      isClicked: isClickedFromOutsideState,
      width = '100%',
      isTopVariant = false,
      isChecked: isCheckedFromOutsideState,
      isSmallVariant = false,
      label = 'Label',
      listItemTitle = 'List Item Title',
      rightContent = '',

      ...props
    },
    ref,
  ) => {
    const [isClicked, setIsClicked] = useState(false);
    const [isChecked, setIsChecked] = useState(false);

    useEffect(() => {
      if (!isClickable) return;
      setIsClicked(() => !!isClickedFromOutsideState);
    }, [isClickable, isClickedFromOutsideState]);

    useEffect(() => {
      setIsChecked(() => !!isCheckedFromOutsideState);
    }, [isCheckedFromOutsideState]);

    const isClickedHandler = useCallback(() => {
      if (!isClickable) return;

      setIsClicked((curr) => {
        onClickCallback?.(!curr);
        return !curr;
      });
    }, [isClickable, onClickCallback]);

    const isCheckedHandler = useCallback(() => {
      setIsChecked((curr) => {
        onCheckCallback?.(!curr);
        return !curr;
      });
    }, [onCheckCallback]);

    return (
      <StyledCardList
        {...props}
        ref={ref}
        onClick={isClickedHandler}
        isClicked={isClicked}
        isClickable={isClickable}
        width={width}
        isSmallVariant={isSmallVariant}
        isTopVariant={isTopVariant}
        isChecked={isChecked}
        data-state={isClicked ? 'clicked' : 'idle'}
        data-testid="CardList"
      >
        <section>
          <div>
            {isTopVariant && <Icon name="star" iconStyle="filled" color="action" fontSize="inherit" />}
            {listItemTitle}
          </div>
          {!isSmallVariant && <div className="label">{label}</div>}
        </section>

        {rightContent && (
          <section>
            {rightContent === 'checkbox' && (
              <Switch
                checked={isChecked}
                onChange={isCheckedHandler}
                inputProps={{ 'aria-label': 'controlled' }}
              ></Switch>
            )}
            {rightContent === 'chevron-right' && <Icon name="chevron-right" fontSize="inherit" />}
          </section>
        )}
      </StyledCardList>
    );
  },
);

const StyledCardList = styled('div')<{
  isClicked?: boolean;
  isClickable?: boolean;
  width?: string;
  isTopVariant?: boolean;
  isChecked?: boolean;
  label?: string;
  isSmallVariant?: boolean;
  rightContent?: 'checkbox' | 'chevron-right';
}>(({ theme, ...args }) => {
  const defaultBoxShadow = ``;

  const stylesBasedOnClick = args.isClicked ? {} : { boxShadow: defaultBoxShadow };

  const stylesBasedOnIsClickable = args.isClickable
    ? { cursor: 'pointer', ...stylesBasedOnClick }
    : { boxShadow: defaultBoxShadow };

  const stylesBasedOnIsSmallVariant = args.isSmallVariant
    ? { padding: '14px 0 20px 0', height: '24px' }
    : { padding: '18px 0', height: '56px', maxHeight: '56px' };

  let styles = {};
  styles = {
    width: args.width,
    display: 'flex',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
    borderRadius: '0px',
    marginBottom: '7px',
    borderBottom: '1px solid ',
    borderBottomColor: theme.palette.onSurface.os100,
    alignItems: 'center',

    '&:hover': {
      border: '',
    },
    section: {
      display: 'flex',
      alignContent: 'center',
      flexWrap: 'wrap',
      flexDirection: 'column',
      fontWeight: 'bold',
    },
    '.sa-i': {
      padding: '2px 0 0 0',
      marginRight: '6px',
    },
    '.label': {
      fontSize: '10px',
      fontWeight: 'normal',
      color: theme.palette.onSurface.os300,
    },
    ...stylesBasedOnIsClickable,
    ...stylesBasedOnIsSmallVariant,
  };

  return styles;
});
